import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as ArrowBackIcon,
  LocalShipping as TruckIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../API/api';
import { toast } from 'react-toastify';

const CreateTripRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [formData, setFormData] = useState({
    Expediteur: '',
    Destinataire: '',
    Date_chargement: '',
    Date_déchargement: '',
    Type_de_moyen: '',
    Type_Voyage: '',
    Nombre_palettes: '',
    BL_Number: '',
    Fournisseur: '',
    notes: '',
  });

  // Récupérer le token depuis sessionStorage
  const getToken = useCallback(() => {
    try {
      const storedAuthState = sessionStorage.getItem('authLocalstorage');
      if (storedAuthState) {
        const parsedAuthState = JSON.parse(storedAuthState);
        return parsedAuthState?.token;
      }
    } catch (e) {
      console.error('Error parsing auth state:', e);
    }
    return null;
  }, []);

  // Vérifier l'authentification au montage
  React.useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/');
    }
  }, [getToken, navigate]);

  // Charger les informations du client
  React.useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${API}/client-portal/client-info`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });

        if (response.data && response.data.status) {
          setClientInfo(response.data.data || {});
        } else {
          console.error('Client info error:', response.data);
          setClientInfo({});
        }
      } catch (error) {
        console.error('Error fetching client info:', error);
        setClientInfo({}); // Set empty object to prevent undefined errors
        toast.error('Erreur lors du chargement des informations du client');
      }
    };

    fetchClientInfo();
  }, [getToken]);

  const handleChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation des champs requis
      if (!formData.Expediteur || !formData.Destinataire || !formData.Date_chargement || 
          !formData.Type_de_moyen || !formData.Type_Voyage) {
        setError('Veuillez remplir tous les champs obligatoires');
        setLoading(false);
        return;
      }

      const token = getToken();
      if (!token) {
        setError('Session expirée. Veuillez vous reconnecter.');
        navigate('/');
        return;
      }
      
      // Préparer les données pour l'API
      // Convertir la date au format ISO pour l'API
      const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return null;
        // Convertir "YYYY-MM-DDTHH:mm" en "YYYY-MM-DD HH:mm:ss"
        return dateTimeString.replace('T', ' ') + ':00';
      };

      const tripData = {
        Expediteur: formData.Expediteur,
        Destinataire: formData.Destinataire,
        Date_chargement: formatDateTime(formData.Date_chargement),
        Date_déchargement: formData.Date_déchargement ? formatDateTime(formData.Date_déchargement) : null,
        Type_de_moyen: formData.Type_de_moyen,
        Type_Voyage: formData.Type_Voyage,
        Nombre_palettes: formData.Nombre_palettes ? parseFloat(formData.Nombre_palettes) : null,
        BL_Number: formData.BL_Number || null,
        Fournisseur: formData.Fournisseur || null,
      };

      const response = await axios.post(
        `${API}/client-portal/trip-request`,
        tripData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.status) {
        toast.success('Demande de voyage créée avec succès');
        navigate('/dashboard');
      } else {
        setError(response.data.message || 'Erreur lors de la création de la demande');
      }
    } catch (err) {
      console.error('Error creating trip request:', err);
      
      // Si erreur 401, rediriger vers login
      if (err.response?.status === 401) {
        sessionStorage.removeItem('authLocalstorage');
        toast.error('Session expirée. Veuillez vous reconnecter.');
        navigate('/');
        return;
      }

      let errorMessage = 'Erreur lors de la création de la demande de voyage';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        if (typeof errors === 'object') {
          errorMessage = Object.values(errors).flat().join(', ');
        } else {
          errorMessage = Array.isArray(errors) ? errors.join(', ') : errors;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#1f2937',
                '&:hover': {
                  backgroundColor: 'rgba(60, 33, 81, 0.08)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            {clientInfo?.logo && (
              <Avatar
                src={clientInfo.logo}
                alt={clientInfo.group_name || clientInfo.client_name}
                sx={{ 
                  width: 48, 
                  height: 48,
                  borderRadius: 1,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                }}
                variant="rounded"
              />
            )}
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  color: '#1f2937',
                  letterSpacing: '-0.025em',
                }}
              >
                Nouvelle Demande de Voyage
              </Typography>
              {clientInfo?.client_name && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#6b7280',
                    fontSize: '12px',
                  }}
                >
                  {clientInfo.client_name}
                </Typography>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06)',
            background: '#ffffff',
            border: '1px solid rgba(229, 231, 235, 0.8)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(60, 33, 81, 0.3)',
                }}
              >
                <TruckIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#1f2937',
                    letterSpacing: '-0.02em',
                    mb: 0.5,
                  }}
                >
                  Créer une Demande de Voyage
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6b7280',
                    fontSize: '0.875rem',
                  }}
                >
                  Remplissez le formulaire ci-dessous pour soumettre votre demande
                </Typography>
              </Box>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 1,
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Origine */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Expéditeur (Origine) *"
                    placeholder="Adresse ou ville de chargement"
                    value={formData.Expediteur}
                    onChange={handleChange('Expediteur')}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>

                {/* Destination */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Destinataire (Destination) *"
                    placeholder="Adresse ou ville de déchargement"
                    value={formData.Destinataire}
                    onChange={handleChange('Destinataire')}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>

                {/* Date de chargement */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date de Chargement *"
                    type="datetime-local"
                    value={formData.Date_chargement}
                    onChange={handleChange('Date_chargement')}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>

                {/* Date de déchargement */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date de Déchargement (optionnel)"
                    type="datetime-local"
                    value={formData.Date_déchargement}
                    onChange={handleChange('Date_déchargement')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>

                {/* Type de moyen */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Type de Moyen *</InputLabel>
                    <Select
                      value={formData.Type_de_moyen}
                      label="Type de Moyen *"
                      onChange={handleChange('Type_de_moyen')}
                      sx={{
                        borderRadius: 1,
                      }}
                    >
                      <MenuItem value="T1">T1 - Véhicule léger</MenuItem>
                      <MenuItem value="T2">T2 - Camionnette</MenuItem>
                      <MenuItem value="T3">T3 - Camion 3.5T</MenuItem>
                      <MenuItem value="T4">T4 - Camion 7T</MenuItem>
                      <MenuItem value="T5">T5 - Camion 14T</MenuItem>
                      <MenuItem value="T6">T6 - Semi-remorque</MenuItem>
                  </Select>
                </FormControl>
                </Grid>

                {/* Type de voyage */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Type de Voyage *</InputLabel>
                    <Select
                      value={formData.Type_Voyage}
                      label="Type de Voyage *"
                      onChange={handleChange('Type_Voyage')}
                      sx={{
                        borderRadius: 1,
                      }}
                    >
                      <MenuItem value="Export">Export</MenuItem>
                      <MenuItem value="Import">Import</MenuItem>
                      <MenuItem value="National">National</MenuItem>
                      <MenuItem value="Aller retour">Aller retour</MenuItem>
                  </Select>
                </FormControl>
                </Grid>

                {/* Nombre de palettes */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre de Palettes"
                    type="number"
                    placeholder="0"
                    value={formData.Nombre_palettes}
                    onChange={handleChange('Nombre_palettes')}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>

                {/* Numéro BL */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Numéro BL (optionnel)"
                    placeholder="BL-XXXXX"
                    value={formData.BL_Number}
                    onChange={handleChange('BL_Number')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>

                {/* Fournisseur */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fournisseur (optionnel)"
                    placeholder="Nom du fournisseur"
                    value={formData.Fournisseur}
                    onChange={handleChange('Fournisseur')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes ou Instructions (optionnel)"
                    placeholder="Informations complémentaires sur votre demande..."
                    value={formData.notes}
                    onChange={handleChange('notes')}
                    multiline
                    rows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Boutons d'action */}
              <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: '1px solid #e5e7eb' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1,
                    px: 3,
                    py: 1.5,
                    borderColor: '#e5e7eb',
                    color: '#1f2937',
                    '&:hover': {
                      borderColor: '#3c2151',
                      backgroundColor: 'rgba(60, 33, 81, 0.05)',
                    },
                  }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                    boxShadow: '0 2px 8px rgba(60, 33, 81, 0.3)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2a173a 0%, #3c2151 100%)',
                      boxShadow: '0 4px 12px rgba(60, 33, 81, 0.4)',
                    },
                    '&:disabled': {
                      background: '#9ca3af',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {loading ? 'Création en cours...' : 'Soumettre la Demande'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CreateTripRequest;
